import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { EventService } from '../../services/event.service';
import { AttendeeService } from '../../services/attendee.service';
import { GenericEvent } from '../../models/event.model';
import { RegistrationFormData, CallPlan } from '../../models/attendee.model';
import { EvaluationQuestion } from '../../models/question.model';
import { environment } from '../../../environments/environment';
import { finalize, takeUntil, catchError, map } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { US_STATES, DEGREE_OPTIONS, HCP_OPTIONS } from '../../constants/options.constants';
import { MultiSelectChipsComponent } from '../multi-select-chips/multi-select-chips';
import { AttendeeResponse } from '../../models/backend-response.model';

function arrayRequired(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (Array.isArray(value) && value.length > 0) return null;
  if (!Array.isArray(value) && value) return null;
  return { required: true };
}

@Component({
  selector: 'app-registration-modal',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MultiSelectChipsComponent],
  templateUrl: './registration-modal.html',
  styleUrl: './registration-modal.css'
})
export class RegistrationModalComponent implements OnInit, OnDestroy {
  @Input() event!: GenericEvent;
  @Output() submitted = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  step = 1;
  loading = false;
  checkingCallPlan = false;
  error: string | null = null;
  missingFields: string[] = [];
  isCallPlanVerified = false;

  registrationForm: FormGroup;
  confirmDialogVisible = false;
  callPlanData: CallPlan | null = null;

  readonly hcpOptions = HCP_OPTIONS;
  degreeOptions: string[] = [];
  stateOptions: string[] = [];
  specialtyOptions: string[] = [];
  contactTypeOptions: string[] = [];

  registrationQuestions: EvaluationQuestion[] = [];
  loadingQuestions = false;
  useDynamicQuestions = false;
  /** questionId → N/A checked for NPI fields */
  npiNaByQuestionId: Record<number, boolean> = {};
  /** Hardcoded-form NPI N/A */
  npiNa = false;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private eventService: EventService,
    private attendeeService: AttendeeService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {
    this.registrationForm = this.createForm();
  }

  ngOnInit() {
    // Fetch dropdown options using companyId from the event (not from environment)
    if (this.event.companyId) {
      this.eventService.getCompanyHcpDropdown(this.event.companyId, 'attendee_degree_hcp').pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.degreeOptions = [...DEGREE_OPTIONS];
          return of([]);
        })
      ).subscribe(options => {
        if (options && options.length > 0) {
          this.degreeOptions = options.map(opt => opt.value || opt.label || '').filter(opt => opt !== '');
        } else {
          this.degreeOptions = [...DEGREE_OPTIONS];
        }
        this.cdr.detectChanges();
      });

      this.eventService.getCompanyDropdown(this.event.companyId, 'attendee_specialty').pipe(
        takeUntil(this.destroy$),
        catchError(() => of([]))
      ).subscribe(options => {
        if (options && options.length > 0) {
          this.specialtyOptions = options.map(opt => opt.value || opt.label || '').filter(opt => opt !== '');
        }
        this.cdr.detectChanges();
      });
    }

    // Fetch states
    this.eventService.getCommonDropdown('country_state').pipe(
      takeUntil(this.destroy$),
      catchError(() => {
        return this.http.get<Array<{ name: string; code: string }>>(`${environment.apiUrl}/api/states?country_id=US`).pipe(
          map(states => states.map(s => ({ value: s.name, label: s.name }))),
          catchError(() => {
            this.stateOptions = [...US_STATES];
            return of([]);
          })
        );
      })
    ).subscribe(options => {
      if (options && options.length > 0) {
        this.stateOptions = options.map(opt => opt.value || opt.label || '').filter(opt => opt !== '');
      } else {
        this.stateOptions = [...US_STATES];
      }
      this.cdr.detectChanges();
    });

    // Fetch registration questions
    this.loadRegistrationQuestions(this.event.id, this.event.token);

    if (this.event.attendee) {
      this.prefillFromAttendee(this.event.attendee);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createForm(): FormGroup {
    return this.fb.group({
      step: [1],
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      is_hcp: ['', Validators.required],
      cats_num: [''],
      license_state: [''],
      license: [''],
      degrees: [[] as string[], arrayRequired],
      degrees_other: [''],
      subspecialty: [[] as string[], arrayRequired],
      subspecialty_other: [''],
      contact_type: ['', Validators.required],
      contact_type_other: [''],
      office: this.fb.group({
        affiliation: [''],
        address1: ['', Validators.required],
        address2: [''],
        city: ['', Validators.required],
        state: ['', Validators.required],
        zip: ['', Validators.required],
        phone: [''],
        email: ['', [Validators.required, Validators.email]]
      }),
      confirm_email: ['', [Validators.required, Validators.email]],
      auto_populate: ['0'],
      compass_id: [''],
      call_plan_id: [null]
    });
  }

  get isHcpLicensed(): boolean {
    return this.registrationForm.get('is_hcp')?.value === 'HCP_LICENSED';
  }

  get isHcpNotLicensed(): boolean {
    return this.registrationForm.get('is_hcp')?.value === 'HCP_NOT_LICENSED';
  }

  get showHcpFields(): boolean {
    return this.isHcpLicensed || this.isHcpNotLicensed;
  }

  get showDegreesOther(): boolean {
    const val = this.registrationForm.get('degrees')?.value;
    return Array.isArray(val) ? val.includes('Other') : val === 'Other';
  }

  get showSubspecialtyOther(): boolean {
    const val = this.registrationForm.get('subspecialty')?.value;
    return Array.isArray(val) ? val.includes('Other') : val === 'Other';
  }

  get showContactTypeOther(): boolean {
    return this.registrationForm.get('contact_type')?.value === 'Other';
  }

  onStep1Submit(event?: SubmitEvent | null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.useDynamicQuestions) {
      const invalidDynamicFields: string[] = [];
      this.registrationQuestions.forEach(eq => {
        const question = eq.question;
        if (!question) return;
        if (this.isQuestionRequired(eq) && this.isQuestionVisible(eq)) {
          const controlName = this.getQuestionControlName(question.id);
          const control = this.registrationForm.get(controlName);
          if (control) {
            const value = control.value;
            const isEmpty = value === null || value === '' || value === undefined ||
              (Array.isArray(value) && value.length === 0);
            if (control.invalid || isEmpty) {
              invalidDynamicFields.push(controlName);
              control.markAsTouched();
            }
          }
        }
      });

      // Validate confirm fields
      for (const eq of this.registrationQuestions) {
        const question = eq.question;
        if (!question || !question.confirm || !this.isQuestionVisible(eq)) continue;
        if (this.isConfirmRequired(eq)) {
          const confirmControl = this.registrationForm.get(`confirm_question_${question.id}`);
          if (confirmControl && !confirmControl.value?.toString().trim()) {
            invalidDynamicFields.push(`confirm_question_${question.id}`);
            confirmControl.markAsTouched();
          }
        }
        const confirmError = this.getConfirmError(eq);
        if (confirmError) {
          this.error = confirmError;
          this.markFieldsAsMissing([`confirm_question_${question.id}`]);
          this.cdr.detectChanges();
          return;
        }
      }

      if (invalidDynamicFields.length > 0) {
        this.markFieldsAsMissing(invalidDynamicFields);
        this.error = 'Please complete all required fields.';
        this.cdr.detectChanges();
        return;
      }

      this.submitRegistration();
    } else {
      const requiredFields = ['first_name', 'last_name', 'is_hcp'];
      const invalidFields = requiredFields.filter(field =>
        this.registrationForm.get(field)?.invalid
      );

      if (invalidFields.length > 0) {
        this.markFieldsAsMissing(invalidFields);
        return;
      }

      const isHcp = this.registrationForm.get('is_hcp')?.value;
      if (isHcp === 'HCP_LICENSED' || isHcp === 'HCP_NOT_LICENSED') {
        if (!this.validateHcpFields(isHcp)) {
          return;
        }
        this.checkCallPlan(this.registrationForm.value);
      } else {
        this.moveToStep2();
      }
    }
  }

  private validateHcpFields(isHcp: string): boolean {
    if (isHcp !== 'HCP_LICENSED') return true;
    if (this.npiNa) return true;
    const catsNum = this.registrationForm.get('cats_num')?.value;
    if (!catsNum) {
      this.error = 'Must enter NPI Number or check N/A.';
      return false;
    }
    return true;
  }

  private moveToStep2() {
    setTimeout(() => {
      this.step = 2;
      this.registrationForm.patchValue({ step: 2 });
      this.cdr.detectChanges();
    }, 0);
  }

  checkCallPlan(formData: RegistrationFormData) {
    const catsNum = formData.cats_num;
    const license = formData.license;
    const licenseState = formData.license_state;

    if (catsNum || (license && licenseState)) {
      setTimeout(() => {
        this.checkingCallPlan = true;
        this.error = null;
        this.cdr.detectChanges();
      }, 0);

      this.attendeeService.getCallPlan(this.event.companyId, {
        cats_num: catsNum,
        first_name: formData.first_name,
        last_name: formData.last_name,
        license: license,
        license_state: licenseState,
        is_hcp: formData.is_hcp
      }).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (callPlan) => {
          this.checkingCallPlan = false;
          if (callPlan) {
            this.callPlanData = callPlan;
            this.confirmDialogVisible = true;
          } else {
            this.moveToStep2();
          }
        },
        error: () => {
          this.checkingCallPlan = false;
          this.moveToStep2();
        }
      });
    } else {
      this.moveToStep2();
    }
  }

  confirmCallPlan() {
    if (!this.callPlanData) {
      this.confirmDialogVisible = false;
      this.moveToStep2();
      return;
    }

    const patchData: Record<string, unknown> = {
      'compass_id': this.callPlanData.compass_id || '',
      'call_plan_id': this.callPlanData.id ? Number(this.callPlanData.id) : null,
      'auto_populate': '1',
      'step': 2,
      'license_state': this.getFormValueOrCallPlan('license_state', this.callPlanData.license_state),
      'license': this.getFormValueOrCallPlan('license', this.callPlanData.license),
      'degrees': this.getFormValueOrCallPlan('degrees', this.callPlanData.degrees),
      'subspecialty': this.getFormValueOrCallPlan('subspecialty', this.callPlanData.specialty),
      'contact_type': this.getFormValueOrCallPlan('contact_type', this.callPlanData.contact_type),
      'office': {
        'affiliation': this.getFormValueOrCallPlan('office.affiliation', this.callPlanData.affiliation),
        'address1': this.getFormValueOrCallPlan('office.address1', this.callPlanData.address1),
        'address2': this.getFormValueOrCallPlan('office.address2', this.callPlanData.address2),
        'city': this.getFormValueOrCallPlan('office.city', this.callPlanData.city),
        'state': this.getFormValueOrCallPlan('office.state', this.callPlanData.state),
        'zip': this.getFormValueOrCallPlan('office.zip', this.callPlanData.zip),
        'email': this.registrationForm.get('office.email')?.value || ''
      },
      'confirm_email': this.registrationForm.get('office.email')?.value || ''
    };

    this.registrationForm.patchValue(patchData);
    this.clearValidatorsForCallPlanFields();

    this.confirmDialogVisible = false;
    this.isCallPlanVerified = true;
    this.submitRegistration();
  }

  private getFormValueOrCallPlan(fieldPath: string, callPlanValue?: string): string {
    return callPlanValue || this.registrationForm.get(fieldPath)?.value || '';
  }

  private clearValidatorsForCallPlanFields() {
    const fieldsToMakeOptional = ['degrees', 'subspecialty', 'contact_type', 'confirm_email'];
    fieldsToMakeOptional.forEach(field => {
      const control = this.registrationForm.get(field);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });

    const officeFields = ['affiliation', 'address1', 'city', 'state', 'zip', 'email'];
    officeFields.forEach(field => {
      const control = this.registrationForm.get(`office.${field}`);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });
  }

  cancelCallPlan() {
    this.registrationForm.patchValue({ auto_populate: '0' });
    if (this.callPlanData) {
      this.registrationForm.patchValue({
        'cats_num': '',
        'license_state': '',
        'license': ''
      });
    }
    this.confirmDialogVisible = false;
    this.moveToStep2();
  }

  onStep2Submit(event?: SubmitEvent | null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.useDynamicQuestions) {
      const invalidDynamicFields: string[] = [];
      this.registrationQuestions.forEach(eq => {
        const question = eq.question;
        if (!question) return;
        if (this.isQuestionRequired(eq) && this.isQuestionVisible(eq)) {
          const controlName = this.getQuestionControlName(question.id);
          const control = this.registrationForm.get(controlName);
          if (control) {
            const value = control.value;
            const isEmpty = value === null || value === '' || value === undefined ||
              (Array.isArray(value) && value.length === 0);
            if (control.invalid || isEmpty) {
              invalidDynamicFields.push(controlName);
              control.markAsTouched();
            }
          }
        }
      });

      if (invalidDynamicFields.length > 0) {
        this.markFieldsAsMissing(invalidDynamicFields);
        this.error = 'Please complete all required fields.';
        this.cdr.detectChanges();
        return;
      }
    } else {
      if (this.registrationForm.invalid) {
        this.markMissingFields();
        this.cdr.detectChanges();
        return;
      }
    }

    this.submitRegistration();
  }

  submitRegistration() {
    if (!this.event) {
      this.error = 'Event information is missing. Please refresh the page and try again.';
      return;
    }

    if (!this.validateRegistration()) {
      this.cdr.detectChanges();
      return;
    }

    this.loading = true;
    this.error = null;
    this.cdr.detectChanges();

    let registrationData: RegistrationFormData;

    if (this.useDynamicQuestions) {
      registrationData = this.buildRegistrationDataFromDynamicQuestions();
    } else {
      const formValue = this.registrationForm.value;
      registrationData = {
        ...formValue,
        step: 2,
        degrees: Array.isArray(formValue.degrees) ? formValue.degrees.join(', ') : formValue.degrees || '',
        subspecialty: Array.isArray(formValue.subspecialty) ? formValue.subspecialty.join(', ') : formValue.subspecialty || '',
        'office.affiliation': formValue.office?.affiliation || '',
        'office.address1': formValue.office?.address1 || '',
        'office.address2': formValue.office?.address2 || '',
        'office.city': formValue.office?.city || '',
        'office.state': formValue.office?.state || '',
        'office.zip': formValue.office?.zip || '',
        'office.phone': formValue.office?.phone || '',
        'office.email': formValue.office?.email || ''
      };
    }

    this.attendeeService.registerAttendee(this.event.companyId, this.event.id, registrationData, this.event.token).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (attendeeId) => {
        if (this.useDynamicQuestions && this.registrationQuestions.length > 0 && attendeeId && this.event) {
          const answers: Record<number, any> = {};
          this.registrationQuestions.forEach(eq => {
            const question = eq.question;
            if (!question) return;
            const controlName = this.getQuestionControlName(question.id);
            const control = this.registrationForm.get(controlName);
            if (control && control.value !== null && control.value !== '' &&
              (Array.isArray(control.value) ? control.value.length > 0 : true)) {
              answers[question.id] = control.value;
            }
          });

          if (Object.keys(answers).length > 0) {
            this.attendeeService.saveRegistrationAnswers(
              this.event.companyId, this.event.id, attendeeId, answers, this.event.token
            ).pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: () => this.submitted.emit(),
              error: () => this.submitted.emit()
            });
          } else {
            this.submitted.emit();
          }
        } else {
          this.submitted.emit();
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err.error?.message || err.message || 'An error occurred while submitting your registration. Please try again.';
        this.cdr.detectChanges();
      }
    });
  }

  private validateRegistration(): boolean {
    if (this.useDynamicQuestions) {
      const emailControl = this.registrationForm.get('office.email');
      const confirmEmailControl = this.registrationForm.get('confirm_email');

      if (emailControl && confirmEmailControl) {
        const email = emailControl.value?.trim().toLowerCase() || '';
        const confirmEmail = confirmEmailControl.value?.trim().toLowerCase() || '';
        if (email && confirmEmail && email !== confirmEmail) {
          this.error = 'Email Address and Confirm Email Address do not match';
          this.markFieldsAsMissing(['office.email', 'confirm_email']);
          return false;
        }
      }

      const invalidFields: string[] = [];
      this.registrationQuestions.forEach(eq => {
        const question = eq.question;
        if (!question) return;
        if (this.isQuestionRequired(eq) && this.isQuestionVisible(eq)) {
          const controlName = this.getQuestionControlName(question.id);
          const control = this.registrationForm.get(controlName);
          if (control) {
            const value = control.value;
            const isEmpty = value === null || value === '' || value === undefined ||
              (Array.isArray(value) && value.length === 0);
            if (isEmpty) {
              invalidFields.push(controlName);
              control.markAsTouched();
            }
          }
        }
      });

      if (invalidFields.length > 0) {
        this.error = 'Please complete all required fields.';
        this.markFieldsAsMissing(invalidFields);
        return false;
      }

      for (const eq of this.registrationQuestions) {
        const question = eq.question;
        if (!question || !question.confirm || !this.isQuestionVisible(eq)) continue;
        const confirmError = this.getConfirmError(eq);
        if (confirmError) {
          this.error = confirmError;
          this.markFieldsAsMissing([`confirm_question_${question.id}`]);
          return false;
        }
        if (this.isConfirmRequired(eq)) {
          const confirmControl = this.registrationForm.get(`confirm_question_${question.id}`);
          if (confirmControl && !confirmControl.value?.toString().trim()) {
            this.error = 'Please complete all required fields.';
            this.markFieldsAsMissing([`confirm_question_${question.id}`]);
            confirmControl.markAsTouched();
            return false;
          }
        }
      }
    } else {
      const email = this.registrationForm.get('office.email')?.value?.trim().toLowerCase() || '';
      const confirmEmail = this.registrationForm.get('confirm_email')?.value?.trim().toLowerCase() || '';

      if (email && confirmEmail && email !== confirmEmail) {
        this.error = 'Email Address and Confirm Email Address do not match';
        this.markFieldsAsMissing(['office.email']);
        return false;
      }

      if (!this.isCallPlanVerified) {
        if (this.registrationForm.invalid) {
          this.markMissingFields();
          return false;
        }
      } else {
        const firstName = this.registrationForm.get('first_name')?.value;
        const lastName = this.registrationForm.get('last_name')?.value;
        if (!firstName || !lastName) {
          this.error = 'First name and last name are required';
          return false;
        }
      }
    }

    return true;
  }

  // === Dynamic question helpers ===

  loadRegistrationQuestions(eventId: number | string, token?: string) {
    this.loadingQuestions = true;
    this.cdr.detectChanges();
    this.eventService.getRegistrationQuestions(this.event.companyId, eventId, token).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.loadingQuestions = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (questions) => {
        if (questions && questions.length > 0) {
          this.registrationQuestions = questions;
          this.useDynamicQuestions = true;
          this.buildDynamicForm();
        } else {
          this.useDynamicQuestions = false;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.useDynamicQuestions = false;
        this.cdr.detectChanges();
      }
    });
  }

  buildDynamicForm() {
    this.registrationQuestions.forEach(eq => {
      const question = eq.question;
      if (!question) return;

      const controlName = `question_${question.id}`;
      if (this.registrationForm.get(controlName)) return;

      const isMultiselect = (question.field_id === 'degrees' || question.field_id === 'specialties');
      const validators = [];
      // Affiliation / State License # / License State are never required on regsite.
      // NPI is always required (N/A checkbox clears the validator in onNpiNaChange).
      const forceRequired =
        this.isNpiField(question.field_id) ||
        (question.always_required && !this.isOptionalRegistrationField(question.field_id));
      if (forceRequired) {
        validators.push(isMultiselect ? arrayRequired : Validators.required);
      }

      let initialValue: any = '';
      if (isMultiselect || question.type_id === 'CHECKBOX' || question.type_id === 'MULTIPLE_CHECK') {
        initialValue = [];
      } else if (question.type_id === 'TF' || question.type_id === 'YN') {
        initialValue = null;
      }

      this.registrationForm.addControl(controlName, this.fb.control(initialValue, validators));

      if (question.confirm) {
        const confirmControlName = `confirm_question_${question.id}`;
        if (!this.registrationForm.get(confirmControlName)) {
          this.registrationForm.addControl(confirmControlName, this.fb.control(''));
        }
      }

      const control = this.registrationForm.get(controlName);
      if (control && (question.field_id === 'specialties' || question.field_id === 'degrees')) {
        control.valueChanges.pipe(
          takeUntil(this.destroy$)
        ).subscribe(() => this.cdr.detectChanges());
      }
    });

    this.prefillDynamicForm();
  }

  getQuestionControlName(questionId: number): string {
    return `question_${questionId}`;
  }

  /**
   * Returns the effective renderer type for a question. The backend currently defines
   * the State License # question as type_id=NUMBER, field_id=license.license, which
   * would render as <input type="number"> with spinners. The license number is an
   * alphanumeric value (e.g. MD12345-A), so we force any license field to TEXT_FIELD.
   */
  resolveQuestionTypeId(question: { type_id?: string; field_id?: string } | null | undefined): string {
    if (!question) return '';
    if (question.field_id === 'license' || question.field_id === 'license.license') {
      return 'TEXT_FIELD';
    }
    return question.type_id || '';
  }

  getQuestionChoices(question: any): string[] {
    if (question.field_id === 'degrees' && this.degreeOptions.length > 0) {
      return this.degreeOptions;
    }
    if (question.field_id === 'specialties' && this.specialtyOptions.length > 0) {
      return this.specialtyOptions;
    }
    if ((question.field_id === 'address.state' || question.field_id === 'license.state') && this.stateOptions.length > 0) {
      return this.stateOptions;
    }

    if (!question.choices) return [];
    try {
      if (typeof question.choices === 'string') {
        if (question.choices.startsWith('[') || question.choices.startsWith('{')) {
          return JSON.parse(question.choices);
        }
        if (question.choices.includes('\n')) {
          return question.choices.split('\n').map((c: string) => c.trim()).filter((c: string) => c !== '');
        }
        return question.choices.split(',').map((c: string) => c.trim());
      }
      return Array.isArray(question.choices) ? question.choices : [];
    } catch {
      if (typeof question.choices === 'string' && question.choices.includes('\n')) {
        return question.choices.split('\n').map((c: string) => c.trim()).filter((c: string) => c !== '');
      }
      return question.choices.split(',').map((c: string) => c.trim());
    }
  }

  isQuestionVisible(eq: EvaluationQuestion): boolean {
    const question = eq.question;
    if (!question || !question.visibility_condition_applied) return true;

    let subjectControl;

    if (question.visibility_condition_subject_id) {
      const subjectQuestion = this.registrationQuestions.find(q =>
        q.question?.id === question.visibility_condition_subject_id
      );
      if (subjectQuestion?.question) {
        subjectControl = this.registrationForm.get(this.getQuestionControlName(subjectQuestion.question.id));
      }
    } else if (question.field_id?.startsWith('other_')) {
      const parentFieldId = question.field_id.replace('other_', '');
      const parentQuestion = this.registrationQuestions.find(q =>
        q.question?.field_id === parentFieldId ||
        q.question?.field_id === parentFieldId.replace(/s$/, '')
      );
      if (parentQuestion?.question) {
        subjectControl = this.registrationForm.get(this.getQuestionControlName(parentQuestion.question.id));
      }
    }

    if (!subjectControl) return true;

    const subjectValue = subjectControl.value;
    const operator = question.visibility_condition_operator;
    const conditionValue = question.visibility_condition_value;
    const visibilityCommand = question.visibility_command || 'Display';

    let conditionMatches = false;

    if (Array.isArray(subjectValue)) {
      if (operator === '===' || operator === '=' || operator === 'Equals') {
        conditionMatches = subjectValue.includes(conditionValue);
      } else if (operator === '!==' || operator === 'Not Equals') {
        conditionMatches = !subjectValue.includes(conditionValue);
      }
    } else {
      if (operator === '===' || operator === '=' || operator === 'Equals') {
        conditionMatches = String(subjectValue) === String(conditionValue);
      } else if (operator === '!==' || operator === 'Not Equals') {
        conditionMatches = String(subjectValue) !== String(conditionValue);
      }
    }

    if (visibilityCommand === 'Hide') {
      return !conditionMatches;
    } else {
      return conditionMatches;
    }
  }

  isOptionalRegistrationField(fieldId?: string | null): boolean {
    return [
      'address.affiliation',
      'affiliation',
      'license',
      'license.license',
      'license.state',
    ].includes(fieldId || '');
  }

  isNpiField(fieldId?: string | null): boolean {
    return fieldId === 'npi_no' || fieldId === 'npi' || fieldId === 'cats_num';
  }

  isNpiNa(questionId: number): boolean {
    return !!this.npiNaByQuestionId[questionId];
  }

  onNpiNaChange(eq: EvaluationQuestion, event: Event) {
    const question = eq.question;
    if (!question) return;
    const checked = !!(event.target as HTMLInputElement)?.checked;
    this.npiNaByQuestionId[question.id] = checked;
    const control = this.registrationForm.get(this.getQuestionControlName(question.id));
    if (!control) return;
    if (checked) {
      control.setValue('');
      control.clearValidators();
    } else {
      control.setValidators([Validators.required]);
    }
    control.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  onHardcodedNpiNaChange(event: Event) {
    this.npiNa = !!(event.target as HTMLInputElement)?.checked;
    const control = this.registrationForm.get('cats_num');
    if (!control) return;
    if (this.npiNa) {
      control.setValue('');
    }
    control.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  isQuestionRequired(eq: EvaluationQuestion): boolean {
    const question = eq.question;
    if (!question) return false;
    if (this.isOptionalRegistrationField(question.field_id)) return false;
    if (this.isNpiField(question.field_id)) {
      return !this.isNpiNa(question.id);
    }

    if (question.always_required) return true;

    if (question.required_if_question_id) {
      const requiredIfControl = this.registrationForm.get(this.getQuestionControlName(question.required_if_question_id));
      if (requiredIfControl) {
        const value = requiredIfControl.value;
        if (question.required_if_value) {
          return String(value) === String(question.required_if_value);
        }
        return !!value;
      }
    }

    return false;
  }

  isConfirmRequired(eq: EvaluationQuestion): boolean {
    const question = eq.question;
    if (!question || !question.confirm) return false;
    if (this.isQuestionRequired(eq)) return true;
    const control = this.registrationForm.get(this.getQuestionControlName(question.id));
    return !!control && !!control.value;
  }

  getConfirmError(eq: EvaluationQuestion): string | null {
    const question = eq.question;
    if (!question || !question.confirm) return null;
    const originalControl = this.registrationForm.get(this.getQuestionControlName(question.id));
    const confirmControl = this.registrationForm.get(`confirm_question_${question.id}`);
    if (!originalControl || !confirmControl) return null;
    const originalValue = (originalControl.value || '').toString().trim();
    const confirmValue = (confirmControl.value || '').toString().trim();
    if (originalValue && confirmValue && originalValue.toLowerCase() !== confirmValue.toLowerCase()) {
      return 'The responses do NOT match';
    }
    return null;
  }

  isChoiceSelected(questionId: number, choice: string): boolean {
    const control = this.registrationForm.get(this.getQuestionControlName(questionId));
    if (control && Array.isArray(control.value)) {
      return control.value.includes(choice);
    }
    return false;
  }

  onCheckboxChange(questionId: number, choice: string, event: any) {
    const control = this.registrationForm.get(this.getQuestionControlName(questionId));
    if (control) {
      const currentValue = control.value || [];
      const target = (event.target as HTMLInputElement);
      if (target?.checked) {
        if (!currentValue.includes(choice)) {
          control.setValue([...currentValue, choice]);
        }
      } else if (target) {
        control.setValue(currentValue.filter((v: string) => v !== choice));
      }
    }
  }

  private buildRegistrationDataFromDynamicQuestions(): RegistrationFormData {
    const registrationData: RegistrationFormData = {
      step: 2,
      first_name: '',
      middle_name: '',
      last_name: '',
      degrees: '',
      degrees_other: '',
      subspecialty: '',
      subspecialty_other: '',
      'office.affiliation': '',
      'office.address1': '',
      'office.address2': '',
      'office.city': '',
      'office.state': '',
      'office.zip': '',
      'office.phone': '',
      'office.email': '',
      confirm_email: '',
      cats_num: '',
      license: '',
      license_state: ''
    };

    this.registrationQuestions.forEach(eq => {
      const question = eq.question;
      if (!question || !question.field_id) return;

      const controlName = this.getQuestionControlName(question.id);
      const control = this.registrationForm.get(controlName);
      if (!control) return;

      const value = control.value;
      const fieldId = question.field_id;

      const fieldMapping: Record<string, string> = {
        'first_name': 'first_name',
        'last_name': 'last_name',
        'npi_no': 'cats_num',
        'cats_num': 'cats_num',
        'degrees': 'degrees',
        'other_degree': 'degrees_other',
        'specialties': 'subspecialty',
        'other_specialties': 'subspecialty_other',
        'address.affiliation': 'office.affiliation',
        'address.address1': 'office.address1',
        'address.address2': 'office.address2',
        'address.city': 'office.city',
        'address.state': 'office.state',
        'address.zip': 'office.zip',
        'address.phone': 'office.phone',
        'address.email': 'office.email',
        'license': 'license',
        'license.license': 'license',
        'license.state': 'license_state'
      };

      const mappedField = fieldMapping[fieldId];
      if (mappedField) {
        if (Array.isArray(value)) {
          (registrationData as any)[mappedField] = (fieldId === 'degrees' || fieldId === 'specialties')
            ? value.join(', ')
            : value[0] || '';
        } else {
          (registrationData as any)[mappedField] = value || '';
        }
      }
    });

    return registrationData;
  }

  private toArray(val: string | string[] | null | undefined): string[] {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    return val.split(',').map(s => s.trim()).filter(Boolean);
  }

  private prefillFromAttendee(att: AttendeeResponse) {
    const license = att.licenses?.[0];
    let isHcp = '';
    if (att.medical_professional || att.hcp_flag) {
      isHcp = license?.license ? 'HCP_LICENSED' : 'HCP_NOT_LICENSED';
    } else if (att.medical_professional === false && !att.hcp_flag) {
      isHcp = 'NOT_HCP';
    }
    const email = att.email || att.address?.email || '';

    this.registrationForm.patchValue({
      first_name: att.first_name || '',
      last_name: att.last_name || '',
      is_hcp: isHcp,
      cats_num: att.npi_no || '',
      license_state: license?.state || '',
      license: license?.license || '',
      degrees: this.toArray(att.degrees),
      subspecialty: this.toArray(att.specialties || att.subspecialty || att.specialty),
      contact_type: att.contact_type || '',
      office: {
        affiliation: att.address?.affiliation || '',
        address1: att.address?.address1 || '',
        address2: att.address?.address2 || '',
        city: att.address?.city || '',
        state: att.address?.state || '',
        zip: att.address?.zip || '',
        phone: att.address?.phone || '',
        email: email
      },
      confirm_email: email
    });
  }

  private prefillDynamicForm() {
    if (!this.event.attendee) return;
    const att = this.event.attendee;
    const license = att.licenses?.[0];
    const email = att.email || att.address?.email || '';
    const fieldValueMap: Record<string, any> = {
      first_name: att.first_name || '',
      middle_name: att.middle_name || '',
      last_name: att.last_name || '',
      npi_no: att.npi_no || '',
      cats_num: att.npi_no || '',
      degrees: this.toArray(att.degrees),
      specialties: this.toArray(att.specialties || att.subspecialty || att.specialty),
      'address.affiliation': att.address?.affiliation || '',
      'address.address1': att.address?.address1 || '',
      'address.address2': att.address?.address2 || '',
      'address.city': att.address?.city || '',
      'address.state': att.address?.state || '',
      'address.zip': att.address?.zip || '',
      'address.phone': att.address?.phone || '',
      email: email,
      'license.license': license?.license || '',
      'license.state': license?.state || '',
    };

    this.registrationQuestions.forEach(eq => {
      const question = eq.question;
      if (!question?.field_id) return;
      const value = fieldValueMap[question.field_id];
      if (value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        const control = this.registrationForm.get(this.getQuestionControlName(question.id));
        if (control) control.patchValue(value);
      }
    });

    const emailQuestion = this.registrationQuestions.find(eq => eq.question?.field_id === 'email' || eq.question?.field_id === 'address.email');
    if (emailQuestion?.question && email) {
      const confirmControl = this.registrationForm.get(`confirm_question_${emailQuestion.question.id}`);
      if (confirmControl) confirmControl.patchValue(email);
    }
  }

  markFieldsAsMissing(fields: string[]) {
    this.missingFields = fields;
    fields.forEach(field => {
      const control = this.registrationForm.get(field);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  markMissingFields() {
    this.missingFields = [];
    Object.keys(this.registrationForm.controls).forEach(key => {
      const control = this.registrationForm.get(key);
      if (control && control.invalid && control.errors) {
        this.missingFields.push(key);
        control.markAsTouched();
      }
    });
  }

  isFieldMissing(fieldName: string): boolean {
    return this.missingFields.includes(fieldName);
  }

  onCancel() {
    this.cancelled.emit();
  }
}
